import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import { MESSAGES } from '~/utils/message';
import OlcButton from '~/components/General/OlcButton';
import FindNewContactsModal from '~/components/General/FindNewContactsModal';
import OlcFormModal from '~/components/General/OlcFormModal';
import OlcTooltip from '~/components/General/OlcTooltip';

import './styles.scss';

const GETTING_STARTED_COLLAPSED_STORAGE_KEY = 'dashboard:getting-started-collapsed';

const getIsGettingStartedCollapsed = () => {
  try {
    return window.localStorage.getItem(GETTING_STARTED_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const GettingStarted = ({
  hasAddedContacts = false,
  hasCreatedMailPiece = false,
  hasPlacedOrder = false,
  isLiveMode = true,
  onHide = () => {},
}) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(getIsGettingStartedCollapsed);
  const [activeGuideStep, setActiveGuideStep] = useState(null);
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);
  const [expandedGuideOption, setExpandedGuideOption] = useState(null);

  const completedCount =
    Number(hasAddedContacts) + Number(hasCreatedMailPiece) + Number(hasPlacedOrder);
  const completionPercent = (completedCount / 3) * 100;
  const allSet = completedCount === 3;

  const setGettingStartedCollapsed = (nextIsCollapsed) => {
    setIsCollapsed(nextIsCollapsed);

    try {
      if (nextIsCollapsed) {
        window.localStorage.setItem(GETTING_STARTED_COLLAPSED_STORAGE_KEY, 'true');
      } else {
        window.localStorage.removeItem(GETTING_STARTED_COLLAPSED_STORAGE_KEY);
      }
    } catch {
      // The visual state still works when browser storage is unavailable.
    }
  };

  const renderStep = ({
    number,
    isComplete,
    completeTitle,
    completeDescription,
    title,
    description,
    actionLabel,
    actionPath,
    actionIcon,
    guideStep,
  }) => (
    <article className={`getting-started-step ${isComplete ? 'is-complete' : ''}`}>
      <div className="getting-started-step-title">
        <span className="getting-started-step-number">
          {isComplete ? <CheckRoundedIcon /> : number}
        </span>
        <h3>{isComplete ? completeTitle : title}</h3>
      </div>
      <p>{isComplete ? completeDescription : description}</p>
      <OlcButton
        type="button"
        variant="primary"
        size="small"
        icon={actionIcon}
        className="getting-started-primary-action"
        onClick={() => {
          if (actionPath) {
            navigate(actionPath);
            return;
          }

          setActiveGuideStep(guideStep);
          setIsGuideModalOpen(true);
        }}
      >
        {actionLabel}
      </OlcButton>
    </article>
  );

  const renderOption = ({
    icon,
    title,
    description,
    actionLabel,
    actionPath,
    guideStep,
    isComplete = false,
  }) => (
    <article className={`getting-started-option ${isComplete ? 'is-complete' : ''}`}>
      <span className="getting-started-option-icon">
        {isComplete ? <CheckRoundedIcon /> : icon}
      </span>
      <div className="getting-started-option-copy">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <OlcTooltip
        arrow
        disableInteractive={guideStep !== 'targetList' || isLiveMode}
        placement="top"
        title={
          guideStep === 'targetList' && !isLiveMode ? MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE : ''
        }
      >
        <span className="getting-started-linkTooltipTarget">
          <button
            type="button"
            className="getting-started-link"
            disabled={guideStep === 'targetList' && !isLiveMode}
            onClick={() => {
              if (guideStep) {
                setActiveGuideStep(guideStep);
                setIsGuideModalOpen(true);
              } else {
                navigate(actionPath);
              }
            }}
          >
            <span>{actionLabel}</span>
            <ArrowForwardRoundedIcon />
          </button>
        </span>
      </OlcTooltip>
    </article>
  );

  const closeGuideModal = () => {
    setIsGuideModalOpen(false);
  };
  const clearGuideModal = () => {
    setActiveGuideStep(null);
    setExpandedGuideOption(null);
  };
  const continueFromGuide = (path, state) => {
    closeGuideModal();
    navigate(path, state ? { state } : undefined);
  };
  const runGuideAction = (action) => continueFromGuide(action.path, action.state);

  const guideModalContent = {
    contacts: {
      title: 'Add your contacts',
      subtitle: 'Choose whether to use a list you already have or build a new targeted audience.',
      note: 'You can add more contacts or build another audience anytime.',
      cardOptions: true,
      expandableOptions: true,
      options: [
        {
          icon: <UploadFileOutlinedIcon />,
          title: 'Import from a file',
          description:
            'Use an existing contact list and continue through the standard import flow.',
          actions: [
            {
              label: 'Upload a file',
              description: 'Continue to the contacts import page.',
              path: '/contacts/import?origin=contacts',
              primary: true,
            },
          ],
        },
        {
          icon: <PersonSearchOutlinedIcon />,
          title: 'Find new contacts',
          description: 'Build a fresh list by choosing the search method that fits your audience.',
          actions: [
            {
              icon: <MyLocationOutlinedIcon />,
              label: 'Radius Around an Address',
              description: 'Enter an address and find contacts within a radius around it.',
              path: '/orders/create-order',
              state: { initialOrderOption: 'find_new', initialFindNewOption: 'addresses' },
            },
            {
              icon: <MapOutlinedIcon />,
              label: 'Search an Area',
              description: 'Pick or draw an area, then filter for the contacts you want.',
              path: '/orders/create-order',
              state: { initialOrderOption: 'find_new', initialFindNewOption: 'locations' },
            },
          ],
        },
      ],
    },
    responseTracking: {
      title: 'Set up response tracking',
      subtitle:
        'Choose how you want to capture responses. You can assign tracking to one or more mailings.',
      cardOptions: true,
      options: [
        {
          icon: <PhoneOutlinedIcon />,
          title: 'Tracking Phone Number',
          description:
            'Use a unique number that forwards to you and logs every call and text message.',
          actions: [
            {
              label: 'Set up phone tracking',
              path: '/phone-tracking',
              primary: true,
            },
          ],
        },
        {
          icon: <QrCode2OutlinedIcon />,
          title: 'QR Code',
          description: 'Print a dynamic QR code on your mail and capture who scans it.',
          actions: [
            {
              label: 'Set up a QR code',
              path: '/engagement/qr-codes',
            },
          ],
        },
      ],
    },
  };
  const activeModal = activeGuideStep ? guideModalContent[activeGuideStep] : null;
  const isFindNewContactsGuideDisabled = activeGuideStep === 'contacts' && !isLiveMode;
  const expandedOption = activeModal?.options.find(
    (option) => option.title === expandedGuideOption && !isFindNewContactsGuideDisabled
  );

  if (isCollapsed) {
    return (
      <section
        id="getting-started"
        className="getting-started-card is-collapsed"
        aria-label="Getting started"
      >
        <button
          type="button"
          className="getting-started-collapsed-trigger"
          aria-label="Expand getting started"
          aria-expanded="false"
          onClick={() => setGettingStartedCollapsed(false)}
        >
          <span className="getting-started-rocket">
            <RocketLaunchOutlinedIcon />
          </span>
          <strong>Get started</strong>
          <div className="getting-started-progress">
            <span style={{ width: `${completionPercent}%` }} />
          </div>
          <span className="getting-started-progress-label">{completedCount} of 3 complete</span>
        </button>
        <div className="getting-started-controls">
          <button
            type="button"
            className="getting-started-icon-button"
            aria-label="Hide getting started"
            aria-controls="getting-started"
            title="Hide getting started"
            onClick={onHide}
          >
            <VisibilityOffOutlinedIcon />
          </button>
          <button
            type="button"
            className="getting-started-icon-button"
            aria-label="Expand getting started"
            aria-controls="getting-started"
            onClick={() => setGettingStartedCollapsed(false)}
          >
            <KeyboardArrowDownRoundedIcon />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="getting-started" className="getting-started-card" aria-label="Getting started">
      <div className="getting-started-expanded-content">
        <div className="getting-started-header">
          <button
            type="button"
            className="getting-started-header-collapse-trigger"
            aria-label="Collapse getting started"
            aria-controls="getting-started"
            onClick={() => setGettingStartedCollapsed(true)}
          />
          <div className="getting-started-header-content">
            {allSet ? (
              <span className="getting-started-complete-icon">
                <CheckRoundedIcon />
              </span>
            ) : null}
            <div className="getting-started-heading-copy">
              <div className="getting-started-heading-row">
                <h2>{allSet ? "You're all set" : 'Get started'}</h2>
              </div>
              <p>
                {allSet
                  ? 'Your first mailing is on its way. Keep going below.'
                  : completedCount === 1
                    ? 'Two steps left to send your first mailing'
                    : completedCount === 2
                      ? 'One step left to send your first mailing'
                      : 'Three steps to send your first mailing'}
              </p>
            </div>
          </div>
          {!isLiveMode ? (
            <div className="getting-started-mode-notice">
              <span className="getting-started-mode-icon">!</span>
              <span className="getting-started-mode-message">
                You&apos;re in <strong>Test mode</strong>. Switch to <strong>Live</strong> to send
                mail.
              </span>
            </div>
          ) : null}
        </div>

        <div className="getting-started-controls">
          <button
            type="button"
            className="getting-started-icon-button"
            aria-label="Hide getting started"
            aria-controls="getting-started"
            title="Hide getting started"
            onClick={onHide}
          >
            <VisibilityOffOutlinedIcon />
          </button>
          <button
            type="button"
            className="getting-started-icon-button"
            aria-label="Collapse getting started"
            aria-controls="getting-started"
            onClick={() => setGettingStartedCollapsed(true)}
          >
            <KeyboardArrowUpRoundedIcon />
          </button>
        </div>

        {completedCount > 0 && !allSet ? (
          <div className="getting-started-progress-row">
            <div className="getting-started-progress">
              <span style={{ width: `${completionPercent}%` }} />
            </div>
            <span className="getting-started-progress-label">{completedCount} of 3 complete</span>
          </div>
        ) : null}

        <div className="getting-started-grid">
          {renderStep({
            number: 1,
            isComplete: hasAddedContacts,
            completeTitle: 'Contacts added',
            completeDescription: 'Your audience is ready.',
            title: 'Add your contacts',
            description:
              'Import a list you already have, or use our list builder to find new contacts by radius or area.',
            actionLabel: 'Add contacts',
            guideStep: 'contacts',
          })}
          {renderStep({
            number: 2,
            isComplete: hasCreatedMailPiece,
            completeTitle: 'Template created',
            completeDescription: 'Your first template is ready.',
            title: 'Create your template',
            description:
              'Start from a template, design your own, or have our team create one for you.',
            actionLabel: 'Create a template',
            actionPath: '/create-template',
          })}
          {renderStep({
            number: 3,
            isComplete: hasPlacedOrder,
            completeTitle: 'Order placed',
            completeDescription: 'Your mail is on its way.',
            title: 'Place your order',
            description:
              'Pick your audience and mail piece, review the details, then send it on its way.',
            actionLabel: 'Place an order',
            actionPath: '/orders/create-order',
            actionIcon: 'add',
          })}
        </div>
        <div className="getting-started-options-grid">
          {renderOption({
            icon: <LocationOnOutlinedIcon />,
            title: 'Need a targeted list?',
            description: allSet
              ? 'Find new contacts by radius or area.'
              : 'Find new homeowners by radius or area.',
            actionLabel: 'Build a list',
            guideStep: 'targetList',
          })}
          {renderOption({
            icon: <TrackChangesRoundedIcon />,
            title: 'Want to track responses?',
            description: allSet ? 'See who calls or scans.' : 'See who calls or scans your mail.',
            actionLabel: 'Set up tracking',
            guideStep: 'responseTracking',
          })}
        </div>
      </div>

      <FindNewContactsModal
        isDisabled={!isLiveMode}
        disabledMessage={MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE}
        open={isGuideModalOpen && activeGuideStep === 'targetList'}
        onClose={closeGuideModal}
        onExited={clearGuideModal}
      />
      <OlcFormModal
        open={isGuideModalOpen && activeGuideStep !== 'targetList'}
        onClose={closeGuideModal}
        title={activeModal?.title || ''}
        subtitle={activeModal?.subtitle}
        className={`getting-started-guide-modal ${
          activeModal?.cardOptions ? 'is-card-choice-modal' : ''
        } ${activeModal?.structuredActions ? 'is-structured-action-modal' : ''}`.trim()}
        dialogProps={{
          onExited: clearGuideModal,
          transitionDuration: { enter: 160, exit: 100 },
        }}
        footer={
          <>
            <span className="getting-started-guide-note">{activeModal?.note}</span>
            <OlcButton type="button" variant="secondary" onClick={closeGuideModal}>
              Cancel
            </OlcButton>
          </>
        }
      >
        <div className="getting-started-guide-options">
          {activeModal?.options.map((option) => {
            const isExpandable = activeModal.expandableOptions && option.actions.length > 1;
            const cardAction =
              activeModal.cardOptions && option.actions.length === 1 ? option.actions[0] : null;
            const isFindNewContactsGuideOption =
              activeGuideStep === 'contacts' && option.title === 'Find new contacts';
            const isOptionDisabled = isFindNewContactsGuideOption && isFindNewContactsGuideDisabled;

            if (activeModal.structuredActions) {
              return (
                <article
                  className={`getting-started-guide-option is-structured ${
                    option.actions.length === 1 ? 'has-single-action' : ''
                  }`}
                  key={option.title}
                >
                  <div className="getting-started-guide-option-heading">
                    <span className="getting-started-guide-option-icon">{option.icon}</span>
                    <div>
                      <h3>{option.title}</h3>
                      <p>{option.description}</p>
                    </div>
                  </div>
                  <div className="getting-started-guide-action-list">
                    {option.actions.map((action) => (
                      <button
                        type="button"
                        className="getting-started-guide-action-card"
                        key={action.label}
                        onClick={() => runGuideAction(action)}
                      >
                        <span className="getting-started-guide-action-copy">
                          <strong>{action.label}</strong>
                          <small>{action.description}</small>
                        </span>
                        <ArrowForwardRoundedIcon className="getting-started-guide-action-arrow" />
                      </button>
                    ))}
                  </div>
                </article>
              );
            }

            return cardAction || isExpandable ? (
              <OlcTooltip
                arrow
                disableInteractive={!isOptionDisabled}
                key={option.title}
                placement="top"
                title={isOptionDisabled ? MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE : ''}
              >
                <span className="getting-started-guide-optionTooltipTarget">
                  <button
                    type="button"
                    className={`getting-started-guide-option is-clickable ${
                      expandedGuideOption === option.title && !isOptionDisabled ? 'is-expanded' : ''
                    } ${isOptionDisabled ? 'is-disabled' : ''}`.trim()}
                    aria-disabled={isOptionDisabled}
                    aria-label={
                      isExpandable
                        ? `${option.title}: show options`
                        : `${option.title}: ${cardAction.label}`
                    }
                    aria-expanded={isExpandable ? expandedGuideOption === option.title : undefined}
                    disabled={isOptionDisabled}
                    onClick={() => {
                      if (isOptionDisabled) return;

                      if (isExpandable) {
                        setExpandedGuideOption(option.title);
                        return;
                      }

                      runGuideAction(cardAction);
                    }}
                  >
                    <span className="getting-started-guide-option-icon">{option.icon}</span>
                    <span className="getting-started-guide-option-title">{option.title}</span>
                    <span className="getting-started-guide-option-description">
                      {isOptionDisabled ? MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE : option.description}
                    </span>
                    {option.bestFor ? (
                      <span className="getting-started-guide-best-for">
                        <strong>Best for:</strong> {option.bestFor}
                      </span>
                    ) : null}
                  </button>
                </span>
              </OlcTooltip>
            ) : (
              <article className="getting-started-guide-option" key={option.title}>
                <span className="getting-started-guide-option-icon">{option.icon}</span>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
                {option.bestFor ? (
                  <p className="getting-started-guide-best-for">
                    <strong>Best for:</strong> {option.bestFor}
                  </p>
                ) : null}
                <div className="getting-started-guide-option-actions">
                  {option.actions.map((action) => (
                    <OlcButton
                      type="button"
                      variant={action.primary ? 'primary' : 'secondary'}
                      size="small"
                      key={action.label}
                      onClick={() => runGuideAction(action)}
                    >
                      {action.label}
                    </OlcButton>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
        {expandedOption ? (
          <div
            className="getting-started-guide-suboptions"
            aria-label={`${expandedOption.title} options`}
          >
            {expandedOption.actions.map((action) => (
              <button
                type="button"
                className="getting-started-guide-option is-clickable"
                key={action.label}
                onClick={() => runGuideAction(action)}
              >
                <span className="getting-started-guide-option-icon">{action.icon}</span>
                <span className="getting-started-guide-option-title">{action.label}</span>
                <span className="getting-started-guide-option-description">
                  {action.description}
                </span>
                {action.bestFor ? (
                  <span className="getting-started-guide-best-for">
                    <strong>Best for:</strong> {action.bestFor}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </OlcFormModal>
    </section>
  );
};

export default GettingStarted;
